import type { ContextMessage } from '../../types/chat'

import { ContextUpdateStrategy } from '@proj-airi/server-sdk'
import { nanoid } from 'nanoid'
import { defineStore, storeToRefs } from 'pinia'
import { ref } from 'vue'

import { client } from '../../composables/api'
import { chatMemoryRepo } from '../../database/repos/chat-memory.repo'
import { useAuthStore } from '../auth'
import { useAiriCardStore } from '../modules/airi-card'

export interface ChatMemoryProfile {
  nickname?: string
  likes: string[]
  habits: string[]
}

export interface ChatMemoryEvent {
  id: string
  summary: string
  source: 'user' | 'assistant'
  createdAt: number
}

export interface ChatMemoryRecord {
  key: string
  userId: string
  characterId: string
  profile: ChatMemoryProfile
  events: ChatMemoryEvent[]
  updatedAt: number
}

const MEMORY_CONTEXT_ID = 'memory:user-profile'
const MAX_MEMORY_EVENTS = 20
const MAX_MEMORY_LIST_ITEMS = 8

function makeMemoryKey(userId: string, characterId: string) {
  return `${userId}::${characterId}`
}

function uniquePush(list: string[], value: string, limit = MAX_MEMORY_LIST_ITEMS) {
  const normalized = value.trim()
  if (!normalized)
    return list
  if (list.includes(normalized))
    return list
  return [...list, normalized].slice(-limit)
}

function trimSentence(text: string, maxLength = 120) {
  const cleaned = text.replace(/\s+/g, ' ').trim()
  if (cleaned.length <= maxLength)
    return cleaned
  return `${cleaned.slice(0, maxLength).trim()}...`
}

function extractNickname(text: string) {
  const patterns = [
    /(?:我叫|我是)\s*([^\s,.!?，。！？]{1,24})/,
    /(?:叫我|你可以叫我)\s*([^\s,.!?，。！？]{1,24})/,
    /(?:my name is|i am|i'm|call me)\s+([\w-]{1,24})/i,
  ]

  for (const pattern of patterns) {
    const match = text.match(pattern)
    if (match?.[1])
      return trimSentence(match[1], 24)
  }
}

function extractLikes(text: string) {
  const likes: string[] = []
  const patterns = [
    /(?:我喜欢|我超爱|我爱)\s*([^，。,.!?！？]{1,40})/g,
    /(?:i like|i love)\s+([^,.!?]{1,40})/gi,
  ]

  for (const pattern of patterns) {
    const matches = text.matchAll(pattern)
    for (const match of matches) {
      if (match[1])
        likes.push(trimSentence(match[1], 40))
    }
  }

  return likes
}

function extractHabits(text: string) {
  const habits: string[] = []
  const patterns = [
    /(?:我通常|我每天|我习惯)\s*([^，。,.!?！？]{1,40})/g,
    /(?:i usually|i often|every day i)\s+([^,.!?]{1,40})/gi,
  ]

  for (const pattern of patterns) {
    const matches = text.matchAll(pattern)
    for (const match of matches) {
      if (match[1])
        habits.push(trimSentence(match[1], 40))
    }
  }

  return habits
}

function buildEventSummary(text: string) {
  const cleaned = trimSentence(text, 100)
  if (!cleaned || cleaned.length < 8)
    return ''
  return cleaned
}

export const useChatMemoryStore = defineStore('chat-memory', () => {
  const auth = useAuthStore()
  const { userId } = storeToRefs(auth)
  const { activeCardId } = storeToRefs(useAiriCardStore())

  const loadedKey = ref('')
  const record = ref<ChatMemoryRecord>()

  async function ensureLoaded() {
    const currentUserId = userId.value
    const characterId = activeCardId.value || 'default'
    const key = makeMemoryKey(currentUserId, characterId)

    if (loadedKey.value === key && record.value)
      return record.value

    loadedKey.value = key
    const existing = await chatMemoryRepo.getRecord(key)
    if (existing) {
      record.value = existing
      return existing
    }

    const now = Date.now()
    const initial: ChatMemoryRecord = {
      key,
      userId: currentUserId,
      characterId,
      profile: {
        likes: [],
        habits: [],
      },
      events: [],
      updatedAt: now,
    }
    record.value = initial
    await chatMemoryRepo.saveRecord(key, initial)
    return initial
  }

  async function persist() {
    if (!record.value)
      return
    record.value.updatedAt = Date.now()
    await chatMemoryRepo.saveRecord(record.value.key, record.value)
  }

  async function rememberUserMessage(message: string) {
    const current = await ensureLoaded()
    const text = message.trim()
    if (!text)
      return

    const nickname = extractNickname(text)
    if (nickname)
      current.profile.nickname = nickname

    for (const like of extractLikes(text))
      current.profile.likes = uniquePush(current.profile.likes, like)

    for (const habit of extractHabits(text))
      current.profile.habits = uniquePush(current.profile.habits, habit)

    const summary = buildEventSummary(text)
    if (summary && !current.events.some(item => item.summary === summary)) {
      current.events.push({
        id: nanoid(),
        summary,
        source: 'user',
        createdAt: Date.now(),
      })
      current.events = current.events.slice(-MAX_MEMORY_EVENTS)

      if (auth.isAuthenticated) {
        void client.api.memory.events.$post({
          json: {
            characterId: current.characterId,
            source: 'user',
            summary,
            externalId: current.events.at(-1)?.id,
            timestampMs: Date.now(),
          },
        }).catch(() => {})
      }
    }

    if (auth.isAuthenticated) {
      void client.api.memory.profile.$post({
        json: {
          characterId: current.characterId,
          nickname: current.profile.nickname,
          likes: current.profile.likes,
          habits: current.profile.habits,
        },
      }).catch(() => {})
    }

    await persist()
  }

  async function rememberAssistantMessage(message: string) {
    const current = await ensureLoaded()
    const text = message.trim()
    if (!text)
      return

    const summary = buildEventSummary(text)
    if (!summary)
      return
    if (current.events.some(item => item.summary === summary))
      return

    current.events.push({
      id: nanoid(),
      summary,
      source: 'assistant',
      createdAt: Date.now(),
    })
    current.events = current.events.slice(-MAX_MEMORY_EVENTS)

    await persist()
  }

  async function rememberTurn(userMessage: string, assistantMessage: string) {
    await rememberUserMessage(userMessage)
    await rememberAssistantMessage(assistantMessage)
  }

  async function createContextMessage() {
    const current = await ensureLoaded()

    const profileLines: string[] = []
    if (current.profile.nickname)
      profileLines.push(`nickname: ${current.profile.nickname}`)
    if (current.profile.likes.length)
      profileLines.push(`likes: ${current.profile.likes.join(', ')}`)
    if (current.profile.habits.length)
      profileLines.push(`habits: ${current.profile.habits.join(', ')}`)

    const recentEvents = [...current.events]
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, 6)
      .map(event => `- [${new Date(event.createdAt).toLocaleDateString()}] (${event.source}) ${event.summary}`)

    const text = [
      'Long-term memory for this user and character:',
      profileLines.length ? profileLines.map(line => `- ${line}`).join('\n') : '- profile: no stable facts yet',
      'Recent memory events:',
      recentEvents.length ? recentEvents.join('\n') : '- no memory events yet',
      'Use memory naturally for continuity. Never claim hidden surveillance or private-device access.',
    ].join('\n')

    return {
      id: nanoid(),
      contextId: MEMORY_CONTEXT_ID,
      strategy: ContextUpdateStrategy.ReplaceSelf,
      text,
      createdAt: Date.now(),
    } satisfies ContextMessage
  }

  return {
    ensureLoaded,
    rememberUserMessage,
    rememberAssistantMessage,
    rememberTurn,
    createContextMessage,
  }
})
